import React from "react";
import { useTranslation } from "react-i18next";
import "./css/solutions.css";
import pcbHero from "./img/pcb1.webp";
import { ensureNamespace } from "../../i18n/ensurePageI18n";

const loadI18n = (i18n) => {
  ensureNamespace(i18n, 'pcb', {
    en: {
      sectionTitle: "PCB",
      sectionSubtitle: "AI Machine Vision for Advanced PCB Material Inspection",
      title: "PCB",
      heroDesc: "Wintriss Engineering machine vision systems use AI software in the detection and classification of defects in advanced material analysis as seen in the PCB industry. After standard defects and categories are defined, the system continues to learn to provide the ultimate custom solution.",
      typicalProductsTitle: "Typical Products",
      typicalProductsContent: "Fiberglass, impregnated fiberglass, copper foil, polyimide film (PI), Flexible Copper Clad Laminate (FCCL), and CCL copper base substrate.",
      fiberglassTitle: "Fiberglass",
      fiberglassDefects: "Crazing, black spots, white spots, water mark, pimple, pits, pinholes, crease, rough edge.",
      impregnatedTitle: "Impregnated Fiberglass",
      impregnatedDefects: "Black spots, bugs, fish eyes, pin holes.",
      copperFoilTitle: "Copper Foil",
      copperFoilDefects: "Oxidation spots, bumps, black spots, bugs, stains, pin holes, creases, fold lines.",
      piTitle: "Polyimide Film (PI)",
      piDefects: "Bubbles, contaminants, black spots, white spots, scratches.",
      fcclTitle: "FCCL",
      fcclDefects: "Bubbles, contaminants, black spots, white spots, scratches, oxidation spots, gauge bands.",
      cclTitle: "CCL Copper Base Substrate",
      cclDefects: "Oxidation spots, bumps, bubbles, contaminants, bugs, black spots, scratches, fibers."
    },
    zh: {
      sectionTitle: "PCB",
      sectionSubtitle: "AI 智慧機器視覺檢測先進 PCB 材料",
      title: "PCB",
      heroDesc: "微覺視機器視覺系統利用 AI 軟體對 PCB 產業中的先進材料進行缺陷偵測與分類。當標準缺陷與類別定義後，系統持續學習以提供終極的客製化解決方案。",
      typicalProductsTitle: "典型產品",
      typicalProductsContent: "玻璃纖維、浸漬玻璃纖維、銅箔、PI 聚酰亞胺膜、柔性覆銅板 (FCCL)、以及 CCL 銅基基板。",
      fiberglassTitle: "玻璃纖維",
      fiberglassDefects: "龜裂、黑點、白點、水痕、小凸點、凹坑、針孔、摺痕、毛邊。",
      impregnatedTitle: "浸漬玻璃纖維",
      impregnatedDefects: "黑點、蟲斑、魚眼、針孔。",
      copperFoilTitle: "銅箔",
      copperFoilDefects: "氧化斑、突起、黑點、蟲斑、污斑、針孔、摺痕、折線。",
      piTitle: "PI 聚酰亞胺膜",
      piDefects: "氣泡、異物、黑點、白點、刮傷。",
      fcclTitle: "柔性覆銅板 (FCCL)",
      fcclDefects: "氣泡、異物、黑點、白點、刮傷、氧化斑、厚度帶。",
      cclTitle: "CCL 銅基基板",
      cclDefects: "氧化斑、突起、氣泡、異物、蟲斑、黑點、刮傷、纖維。"
    }
  });
};

function PCB() {
  const { t, i18n } = useTranslation();
  loadI18n(i18n);

  return (
    <div>
      <section className="section-pcb relative bg-gray-900 text-white">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10 flex justify-between items-start">
          <div>
            <h1 className="text-5xl font-bold mb-4">{t("pcb:sectionTitle")}</h1>
            <p className="text-xl text-white">{t("pcb:sectionSubtitle")}</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-3xl font-bold mb-4 tracking-wide">{t("pcb:title")}</h2>
          <p className="leading-relaxed text-sm md:text-base">{t("pcb:heroDesc")}</p>
        </div>
        <div className="flex justify-center">
          <img src={pcbHero} alt={t("pcb:title")} className="rounded-lg shadow-lg w-full max-w-md object-cover" loading="lazy" />
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("pcb:typicalProductsTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("pcb:typicalProductsContent")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6">
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:fiberglassTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:fiberglassDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:impregnatedTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:impregnatedDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:copperFoilTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:copperFoilDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:piTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:piDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:fcclTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:fcclDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-2">{t("pcb:cclTitle")}</h3>
          <p className="text-sm text-gray-700">{t("pcb:cclDefects")}</p>
        </div>
      </section>
    </div>
  );
}

export default PCB;